import { Team } from '@/utils/enums';

/**
 * Calcula las casillas dentro de un área de efecto (AOE).
 *
 * @param {number} centerX - Coordenada X del centro del AOE.
 * @param {number} centerY - Coordenada Y del centro del AOE.
 * @param {object} aoeData - Los datos del AOE extraídos de getAoeInfo().
 * @param {string} aoeData.shape - 'square' o 'circle'.
 * @param {number} aoeData.impactMinRange - Radio mínimo (para donuts).
 * @param {number} aoeData.impactMaxRange - Radio máximo.
 * @param {object} mapBounds - Límites del mapa { width, height }.
 * @returns {Array<{x: number, y: number}>} - Array de coordenadas dentro del AOE.
 */
export default function calculateAoeTiles(centerX, centerY, aoeData, mapBounds = null) {
  if (!aoeData) {
    console.warn("[calculateAoeTiles] aoeData is required");
    return [];
  }

  const { shape, impactMinRange, impactMaxRange } = aoeData;
  const minRange = impactMinRange ?? 0;
  const maxRange = impactMaxRange ?? 0;

  if (maxRange < 0) {
    console.warn("[calculateAoeTiles] impactMaxRange cannot be negative");
    return [];
  }

  const tiles = [];

  // Iterate through potential tiles in bounding box
  for (let dx = -maxRange; dx <= maxRange; dx++) {
    for (let dy = -maxRange; dy <= maxRange; dy++) {
      const x = centerX + dx;
      const y = centerY + dy;

      // Check map bounds if provided
      if (mapBounds) {
        if (x < 0 || x >= mapBounds.width || y < 0 || y >= mapBounds.height) {
          continue;
        }
      }

      // Calculate distance based on shape
      let distance;
      if (shape === 'circle') {
        // Circle uses Manhattan distance (diamond shape)
        distance = Math.abs(dx) + Math.abs(dy);
      } else {
        // Square uses Chebyshev distance (max of abs values)
        // This gives us all 8 adjacent tiles for range 1
        distance = Math.max(Math.abs(dx), Math.abs(dy));
      }

      // Check if within range (supports donut shapes with minRange > 0)
      if (distance >= minRange && distance <= maxRange) {
        tiles.push({ x, y });
      }
    }
  }

  return tiles;
}

/**
 * Filtra unidades que están dentro del AOE según los filtros de targeting.
 *
 * @param {Array} units - Array de unidades del juego.
 * @param {Array} aoeTiles - Tiles del AOE calculados.
 * @param {object} filters - Filtros de targeting (team, state).
 * @param {string} actingUnitTeamClass - teamClass de la unidad que ejecuta ('own', 'ally', 'enemy').
 * @returns {Array} - Unidades que serán afectadas.
 */
export function getUnitsInAoe(units, aoeTiles, filters = {}, actingUnitTeamClass = null) {
  if (!units || !aoeTiles || aoeTiles.length === 0) {
    return [];
  }

  // Create a Set for fast lookup
  const aoeTileSet = new Set(aoeTiles.map(t => `${t.x}-${t.y}`));

  return units.filter(unit => {
    // Check if unit is in AOE area
    if (!aoeTileSet.has(`${unit.x}-${unit.y}`)) {
      return false;
    }

    // Apply team filter if provided
    if (filters.team && filters.team.length > 0 && actingUnitTeamClass) {
      // Determine unit's relationship to acting unit
      const unitTeamRelation = getTeamRelation(unit.teamClass, actingUnitTeamClass);
      if (!filters.team.includes(unitTeamRelation)) {
        return false;
      }
    }

    // Apply state filter if provided
    if (filters.state && filters.state.length > 0) {
      if (filters.state.includes('living') && unit.hp <= 0) {
        return false;
      }
      if (filters.state.includes('dead') && unit.hp > 0) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Determina la relación de equipo entre dos unidades.
 * @param {string} unitTeamClass - teamClass de la unidad objetivo.
 * @param {string} actingTeamClass - teamClass de la unidad actuante.
 * @returns {string} - Team.OWN, Team.ALLY, o Team.ENEMY.
 */
function getTeamRelation(unitTeamClass, actingTeamClass) {
  if (unitTeamClass === actingTeamClass) {
    return Team.OWN;
  }

  // In this game: own is green, ally is blue (same side), enemy is red
  // If acting unit is 'own', then 'ally' units are allies, 'enemy' are enemies
  // If acting unit is 'enemy', then 'own' and 'ally' are enemies to them

  if (actingTeamClass === Team.OWN || actingTeamClass === Team.ALLY) {
    if (unitTeamClass === Team.OWN || unitTeamClass === Team.ALLY) {
      return Team.ALLY;
    }
    return Team.ENEMY;
  }

  // Acting unit is enemy
  if (unitTeamClass === Team.ENEMY) {
    return Team.ALLY;
  }
  return Team.ENEMY;
}
